import type { TrackingPoint } from '../types/locationTracking';

export type QueueStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

type Meta = { chunks: number[]; next: number };

const prefix = (attendanceId: number) => `locationTracking.queue.${attendanceId}`;
const DEFAULT_CHUNK_SIZE = 100;

function parse<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Offline queue for one attendance, persisted in fixed-size chunks so adding a
 * fix rewrites only the newest chunk, not the whole queue. Writes run in order;
 * storage failures never lose the in-memory queue.
 */
export function createTrackingPointQueue(
  storage: QueueStorage,
  attendanceId: number,
  chunkSize = DEFAULT_CHUNK_SIZE,
) {
  const base = prefix(attendanceId);
  const metaKey = `${base}.meta`;
  const chunkKey = (seq: number) => `${base}.${seq}`;
  let chunks: { seq: number; points: TrackingPoint[] }[] = [];
  let next = 0;
  let writes: Promise<void> = Promise.resolve();

  function write(work: () => Promise<void>): Promise<void> {
    writes = writes.then(work).catch(() => undefined);
    return writes;
  }

  function meta(): string {
    return JSON.stringify({ chunks: chunks.map(chunk => chunk.seq), next } satisfies Meta);
  }

  function persistChunk(chunk: { seq: number; points: TrackingPoint[] }, withMeta: boolean) {
    const body = JSON.stringify(chunk.points);
    const metaBody = withMeta ? meta() : null;
    return write(async () => {
      await storage.setItem(chunkKey(chunk.seq), body);
      if (metaBody) await storage.setItem(metaKey, metaBody);
    });
  }

  return {
    async load(): Promise<void> {
      const saved = parse<Meta>(await storage.getItem(metaKey).catch(() => null), {
        chunks: [],
        next: 0,
      });
      chunks = [];
      for (const seq of saved.chunks) {
        const points = parse<TrackingPoint[]>(
          await storage.getItem(chunkKey(seq)).catch(() => null),
          [],
        );
        if (points.length) chunks.push({ seq, points });
      }
      next = Math.max(saved.next, ...chunks.map(chunk => chunk.seq + 1), 0);
    },

    size: () => chunks.reduce((total, chunk) => total + chunk.points.length, 0),

    peek(count: number): TrackingPoint[] {
      const points: TrackingPoint[] = [];
      for (const chunk of chunks) {
        for (const point of chunk.points) {
          if (points.length === count) return points;
          points.push(point);
        }
      }
      return points;
    },

    append(point: TrackingPoint): Promise<void> {
      let tail = chunks[chunks.length - 1];
      const created = !tail || tail.points.length >= chunkSize;
      if (created) {
        tail = { seq: next, points: [] };
        next += 1;
        chunks.push(tail);
      }
      tail.points.push(point);
      return persistChunk(tail, created);
    },

    /** Removes confirmed points; only chunks that changed are rewritten or deleted. */
    remove(clientPointIds: Set<string>): Promise<void> {
      const removed: number[] = [];
      const changed: { seq: number; points: TrackingPoint[] }[] = [];
      chunks = chunks.filter(chunk => {
        const kept = chunk.points.filter(point => !clientPointIds.has(point.clientPointId));
        if (kept.length === chunk.points.length) return true;
        if (!kept.length) {
          removed.push(chunk.seq);
          return false;
        }
        chunk.points = kept;
        changed.push(chunk);
        return true;
      });
      if (!removed.length && !changed.length) return writes;
      const metaBody = meta();
      const bodies = changed.map(chunk => [chunkKey(chunk.seq), JSON.stringify(chunk.points)]);
      return write(async () => {
        for (const [key, body] of bodies) await storage.setItem(key, body);
        for (const seq of removed) await storage.removeItem(chunkKey(seq));
        await storage.setItem(metaKey, metaBody);
      });
    },

    /** Keeps the newest max points when the device has been offline for a long time. */
    trim(max: number): Promise<void> {
      let excess = this.size() - max;
      if (excess <= 0) return writes;
      const dropped = new Set<string>();
      for (const chunk of chunks) {
        for (const point of chunk.points) {
          if (excess <= 0) break;
          dropped.add(point.clientPointId);
          excess -= 1;
        }
      }
      return this.remove(dropped);
    },

    clear(): Promise<void> {
      const seqs = chunks.map(chunk => chunk.seq);
      chunks = [];
      return write(async () => {
        for (const seq of seqs) await storage.removeItem(chunkKey(seq));
        await storage.removeItem(metaKey);
      });
    },

    /** Resolves once every queued write has reached storage. */
    settled: () => writes,
  };
}

export type TrackingPointQueue = ReturnType<typeof createTrackingPointQueue>;
